import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import { setTheme as setThemeInStore } from 'src/features/ui/uiSlice';

export enum Theme {
  Dark = 'dark',
  Light = 'light',
}

const STORAGE_KEY = 'theme';

function isTheme(value: unknown): value is Theme {
  return value === Theme.Dark || value === Theme.Light;
}

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isTheme(stored) ? stored : Theme.Dark;
}

export function useTheme() {
  const dispatch = useAppDispatch();

  const initialTheme = useMemo(getInitialTheme, []);
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useLayoutEffect(() => {
    const root = document.documentElement; // <html> == :root

    root.classList.toggle(Theme.Dark, theme === Theme.Dark);
    root.classList.toggle(Theme.Light, theme === Theme.Light);
    root.style.colorScheme = theme;
    // Boot scripts in index.html set inline hex backgrounds before CSS loads (FOUC).
    // Drop them once the theme class is applied so html/body use --L0-BG from theme.css.
    root.style.removeProperty('background-color');
    document.body.style.removeProperty('background-color');

    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    dispatch(setThemeInStore(theme));
  }, [theme, dispatch]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === Theme.Dark ? Theme.Light : Theme.Dark));
  };

  return { theme, toggleTheme };
}
